import React, {useMemo} from "react";
import {Route} from "@/routing/Router";
import {generateBreadcrumbs} from "@widgets/layout/Breadcrumbs";
import {ActionRow, ActionRowButton, ActionRowMoreButton, Icon, PageHeading} from "peggirkit";
import {FormattedMessage, useIntl} from "react-intl";
import {ArrowPathIcon, CalendarDaysIcon} from "@heroicons/react/24/outline";
import {SetStateFn} from "@/utils/SetStateUtils";
import {useTooltip} from "@widgets/tooltip/TooltipProvider";
import {formatDate} from "@/utils/DateUtils";
import LocationPageMeta from "@widgets/heading/meta/LocationPageMeta";

type Props = {
    route: Route,
    selectedDay: Date,
    setShowDatePicker: SetStateFn<boolean>,
    refresh: () => void,
    isFetching: boolean,
};

const CartTimelinePageHeading = ({route, selectedDay, setShowDatePicker, refresh, isFetching}: Props) => {
    const intl = useIntl();
    const {applyTooltip} = useTooltip();
    const formattedDay = useMemo(() => formatDate(selectedDay), [selectedDay]);

    return (
        <PageHeading
            breadcrumbs={generateBreadcrumbs(route, intl)}
            title={route.name(intl)}
            actionRow={
                <ActionRow
                    useMoreButton={true}
                    moreButton={
                        <ActionRowMoreButton>
                            <FormattedMessage id={"more"}/>
                        </ActionRowMoreButton>
                    }
                    moreButtonPositioning={"end"}
                    breakpoints={{
                        md: {
                            moreButtonPositioning: "start",
                        },
                    }}
                >
                    <ActionRowButton
                        color={"secondary"}
                        iconBefore={<Icon icon={ArrowPathIcon}/>}
                        disabled={isFetching}
                        loading={isFetching}
                        buttonAction={{
                            as: "button",
                            actionProps: {
                                onClick: () => refresh(),
                            },
                        }}
                    >
                        <FormattedMessage id={"refresh"}/>
                    </ActionRowButton>

                    <ActionRowButton
                        color={"primary"}
                        iconBefore={<Icon icon={CalendarDaysIcon}/>}
                        buttonAction={{
                            as: "button",
                            actionProps: {
                                onClick: () => setShowDatePicker(true),
                            },
                        }}
                        props={{
                            ...applyTooltip(intl.formatMessage({id: "pickADay"})),
                        }}
                        ariaProps={{
                            "aria-label": intl.formatMessage({id: "pickADay"}),
                        }}
                    >
                        {formattedDay}
                    </ActionRowButton>
                </ActionRow>
            }
            meta={<LocationPageMeta/>}
            actionRowHorizontal={false}
            breakpoints={{"2xl": {actionRowHorizontal: true}}}
        />
    );
};

export default CartTimelinePageHeading;